import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Snackbar,
  Alert,
  Box,
  Divider,
  Tooltip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
} from '@mui/material';
import PermissionWrapper from '../components/PermissionWrapper';
import axios from 'axios';
import config from '../config';
import CategoryIcon from '@mui/icons-material/Category';
import InventoryIcon from '@mui/icons-material/Inventory';
import StorageIcon from '@mui/icons-material/Storage';
import ImageIcon from '@mui/icons-material/Image';
import BusinessIcon from '@mui/icons-material/Business';
import PriceCheckIcon from '@mui/icons-material/PriceCheck';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import StoreIcon from '@mui/icons-material/Store';
import PlaylistPlayIcon from '@mui/icons-material/PlaylistPlay';
import InfoIcon from '@mui/icons-material/Info';
import SyncAltIcon from '@mui/icons-material/SyncAlt';

interface JobTriggerResponse {
  message: string;
}

type MediaUploadMode = 'api' | 'sftp';

const JobTriggers: React.FC = () => {
  const [loading, setLoading] = useState<string | null>(null);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [mediaUploadMode, setMediaUploadMode] =
    useState<MediaUploadMode>('api');
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const authHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  };

  const fetchMediaUploadMode = async () => {
    setSettingsLoading(true);
    try {
      const response = await axios.get<{ mode: MediaUploadMode }>(
        `${config.API_URL}/jobs/settings/media-upload-mode`,
        { headers: authHeaders() },
      );
      setMediaUploadMode(response.data.mode === 'sftp' ? 'sftp' : 'api');
    } catch (error) {
      setSnackbar({
        open: true,
        message:
          error instanceof Error
            ? error.message
            : 'Kunde inte hämta inställning för media-uppladdning',
        severity: 'error',
      });
    } finally {
      setSettingsLoading(false);
    }
  };

  const updateMediaUploadMode = async (mode: MediaUploadMode) => {
    setSettingsLoading(true);
    try {
      const response = await axios.post<{ mode: MediaUploadMode }>(
        `${config.API_URL}/jobs/settings/media-upload-mode`,
        { mode },
        { headers: authHeaders() },
      );
      setMediaUploadMode(response.data.mode === 'sftp' ? 'sftp' : 'api');
      setSnackbar({
        open: true,
        message:
          response.data.mode === 'sftp'
            ? 'Media-uppladdning: SFTP (fallback) aktiverad'
            : 'Media-uppladdning: API aktiverad',
        severity: 'success',
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message:
          error instanceof Error
            ? error.message
            : 'Kunde inte spara inställning för media-uppladdning',
        severity: 'error',
      });
    } finally {
      setSettingsLoading(false);
    }
  };

  const jobs = [
    {
      endpoint: '/jobs/categories',
      title: 'Uppdatera Kategorier',
      description: 'Uppdaterar alla kategorier i systemet',
      icon: <CategoryIcon sx={{ fontSize: 40 }} />,
      info: 'Hämtar och uppdaterar alla produktkategorier från källsystemet',
    },
    {
      endpoint: '/jobs/products',
      title: 'Uppdatera Produkter',
      description: 'Uppdaterar alla produkter i systemet',
      icon: <InventoryIcon sx={{ fontSize: 40 }} />,
      info: 'Synkroniserar all produktinformation från källsystemet',
    },
    {
      endpoint: '/jobs/stock-balances',
      title: 'Uppdatera Lagersaldon',
      description: 'Uppdaterar alla lagersaldon i systemet',
      icon: <StorageIcon sx={{ fontSize: 40 }} />,
      info: 'Hämtar aktuella lagersaldon för alla produkter',
    },
    {
      endpoint: '/jobs/media',
      title: 'Uppdatera Media',
      description: 'Uppdaterar all produktmedia i systemet',
      icon: <ImageIcon sx={{ fontSize: 40 }} />,
      info: 'Synkroniserar alla produktbilder och media från källsystemet',
    },
    {
      endpoint: '/jobs/suppliers',
      title: 'Uppdatera Leverantörer',
      description: 'Uppdaterar alla leverantörer i systemet',
      icon: <BusinessIcon sx={{ fontSize: 40 }} />,
      info: 'Hämtar och uppdaterar leverantörsinformation',
    },
    {
      endpoint: '/jobs/price-validation',
      title: 'Kontrollera Priser',
      description: 'Kontrollerar och korrigerar priser i systemet',
      icon: <PriceCheckIcon sx={{ fontSize: 40 }} />,
      info: 'Validerar och korrigerar eventuella prisavvikelser',
    },
    {
      endpoint: '/jobs/sales-stats',
      title: 'Uppdatera Försäljningsstatistik',
      description: 'Uppdaterar försäljningsstatistik i systemet',
      icon: <ShowChartIcon sx={{ fontSize: 40 }} />,
      info: 'Beräknar och uppdaterar all försäljningsstatistik',
    },
    {
      endpoint: '/jobs/retail-sales',
      title: 'Uppdatera Butiksförsäljning',
      description: 'Uppdaterar butiksförsäljning i systemet',
      icon: <StoreIcon sx={{ fontSize: 40 }} />,
      info: 'Hämtar och uppdaterar försäljningsdata från butiker',
    },
    {
      endpoint: '/jobs/run-all',
      title: 'Kör Alla Jobb',
      description: 'Kör alla jobb ovan i sekvens',
      icon: <PlaylistPlayIcon sx={{ fontSize: 40 }} />,
      info: 'Kör alla uppdateringsjobb i optimal ordning',
    },
  ];

  const standaloneJobs = [
    {
      endpoint: '/stock-data/update-viskan',
      title: 'Uppdatera Viskan Lager',
      description: 'Skickar lagerdifferenser från Fortnox till Viskan',
      icon: <SyncAltIcon sx={{ fontSize: 40 }} />,
      info: 'Synkroniserar lagersaldon från Fortnox till Viskan för att korrigera eventuella skillnader',
    },
  ];

  const handleTriggerJob = async (endpoint: string, title: string) => {
    setLoading(endpoint);
    try {
      const response = await axios.post<JobTriggerResponse>(
        `${config.API_URL}${endpoint}`,
        {},
        {
          headers: {
            ...authHeaders(),
          },
        },
      );

      setSnackbar({
        open: true,
        message: response.data.message,
        severity: 'success',
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message:
          error instanceof Error
            ? error.message
            : 'Ett fel uppstod vid körning av jobbet',
        severity: 'error',
      });
    } finally {
      setLoading(null);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  useEffect(() => {
    fetchMediaUploadMode();
  }, []);

  return (
    <PermissionWrapper requiredPermissions={['manage_companies']}>
      <Container>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" gutterBottom>
            Systemjobb
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Här kan du manuellt trigga olika systemjobb som normalt körs
            automatiskt enligt schema.
          </Typography>
        </Box>

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              justifyContent="space-between"
            >
              <Box>
                <Typography variant="h6" gutterBottom>
                  Media-uppladdning
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Välj om uppladdning ska gå via API (standard) eller via SFTP
                  (fallback).
                </Typography>
              </Box>

              <FormControl disabled={settingsLoading}>
                <RadioGroup
                  row
                  value={mediaUploadMode}
                  onChange={async (e) => {
                    const value = (e.target as HTMLInputElement).value;
                    const mode: MediaUploadMode =
                      value === 'sftp' ? 'sftp' : 'api';
                    setMediaUploadMode(mode);
                    await updateMediaUploadMode(mode);
                  }}
                >
                  <FormControlLabel
                    value="api"
                    control={<Radio />}
                    label="API"
                  />
                  <FormControlLabel
                    value="sftp"
                    control={<Radio />}
                    label="SFTP"
                  />
                </RadioGroup>
              </FormControl>
            </Stack>
          </CardContent>
        </Card>

        <Grid container spacing={3}>
          {jobs.map((job) => (
            <Grid item xs={12} sm={6} md={4} key={job.endpoint}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: (theme) => theme.shadows[4],
                  },
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    background: 'linear-gradient(90deg, #BB86FC, #03DAC6)',
                  },
                }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      mb: 2,
                      color: 'primary.main',
                    }}
                  >
                    {job.icon}
                    <Box sx={{ ml: 2, flexGrow: 1 }}>
                      <Typography variant="h6" gutterBottom>
                        {job.title}
                      </Typography>
                    </Box>
                    <Tooltip title={job.info} placement="top">
                      <InfoIcon color="action" sx={{ ml: 1 }} />
                    </Tooltip>
                  </Box>
                  <Divider sx={{ my: 1 }} />
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2, minHeight: '40px' }}
                  >
                    {job.description}
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    onClick={() => handleTriggerJob(job.endpoint, job.title)}
                    disabled={loading !== null}
                    startIcon={
                      loading === job.endpoint ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : null
                    }
                  >
                    {loading === job.endpoint ? 'Kör...' : 'Kör jobb'}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Box sx={{ mt: 6, mb: 4 }}>
          <Typography variant="h5" gutterBottom>
            Fristående Jobb
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Dessa jobb körs separat från de automatiska systemjobben.
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {standaloneJobs.map((job) => (
            <Grid item xs={12} sm={6} md={4} key={job.endpoint}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: (theme) => theme.shadows[4],
                  },
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    background: 'linear-gradient(90deg, #FF9800, #FF5722)',
                  },
                }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      mb: 2,
                      color: 'warning.main',
                    }}
                  >
                    {job.icon}
                    <Box sx={{ ml: 2, flexGrow: 1 }}>
                      <Typography variant="h6" gutterBottom>
                        {job.title}
                      </Typography>
                    </Box>
                    <Tooltip title={job.info} placement="top">
                      <InfoIcon color="action" sx={{ ml: 1 }} />
                    </Tooltip>
                  </Box>
                  <Divider sx={{ my: 1 }} />
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2, minHeight: '40px' }}
                  >
                    {job.description}
                  </Typography>
                  <Button
                    variant="contained"
                    color="warning"
                    fullWidth
                    onClick={() => handleTriggerJob(job.endpoint, job.title)}
                    disabled={loading !== null}
                    startIcon={
                      loading === job.endpoint ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : null
                    }
                  >
                    {loading === job.endpoint ? 'Kör...' : 'Kör jobb'}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
            variant="filled"
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </PermissionWrapper>
  );
};

export default JobTriggers;
